import { useEffect, useRef } from 'react';
import { cancelWaitingForJoinIfNeeded, markMeetPageReloadSession } from '../utils';

const POLL_INTERVAL_MS = 3000;

const isReloadShortcut = (event: KeyboardEvent): boolean =>
    event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'r');

type UseWaitingRoomPollParams = {
    isWaitingForApproval: boolean;
    isPaused: boolean;
    connectToRoom: () => Promise<void>;
};

/** Периодический poll joinCallByLink, пока пользователь на экране ожидания одобрения. */
export const useWaitingRoomPoll = ({ isWaitingForApproval, isPaused, connectToRoom }: UseWaitingRoomPollParams) => {
    const isWaitingRef = useRef(isWaitingForApproval);
    const connectToRoomRef = useRef(connectToRoom);

    useEffect(() => {
        isWaitingRef.current = isWaitingForApproval;
    }, [isWaitingForApproval]);

    useEffect(() => {
        connectToRoomRef.current = connectToRoom;
    }, [connectToRoom]);

    useEffect(() => {
        const markReload = () => markMeetPageReloadSession();
        const onKeyDown = (event: KeyboardEvent) => {
            if (isReloadShortcut(event)) {
                markReload();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        if (!isWaitingForApproval || isPaused) {
            return;
        }

        const onBeforeUnload = () => {
            markMeetPageReloadSession();
            void cancelWaitingForJoinIfNeeded(undefined, { keepalive: true });
        };
        window.addEventListener('beforeunload', onBeforeUnload);

        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isWaitingForApproval, isPaused]);

    useEffect(() => {
        if (!isWaitingForApproval || isPaused) {
            return;
        }

        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout>;

        const schedulePoll = () => {
            timeoutId = setTimeout(async () => {
                if (cancelled || !isWaitingRef.current) {
                    return;
                }
                if (document.visibilityState === 'hidden') {
                    schedulePoll();
                    return;
                }

                await connectToRoomRef.current();
                if (!cancelled && isWaitingRef.current) {
                    schedulePoll();
                }
            }, POLL_INTERVAL_MS);
        };

        const onVisibilityChange = () => {
            if (cancelled || document.visibilityState !== 'visible' || !isWaitingRef.current) {
                return;
            }
            clearTimeout(timeoutId);
            void connectToRoomRef.current().then(() => {
                if (!cancelled && isWaitingRef.current) {
                    schedulePoll();
                }
            });
        };

        // Первый poll сразу — иначе approve модератора в первые 3с не подхватится.
        void connectToRoomRef.current().then(() => {
            if (!cancelled && isWaitingRef.current) {
                schedulePoll();
            }
        });
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [isWaitingForApproval, isPaused]);
};
